import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../components/ui/dialog';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Textarea } from '../components/ui/textarea';
import { AlertTriangle, CheckCircle2, DollarSign, MapPin, Bed, Bath, Ruler, Sparkles, FileText } from 'lucide-react';

const VerifyPropertyModal = ({ 
  isOpen, 
  onClose, 
  propertyData, 
  verificationFields = [],
  confidence = {},
  onVerify 
}) => {
  const [formData, setFormData] = useState({
    price: '',
    address: '',
    beds: '',
    baths: '',
    sqft: '',
    property_summary: '',
    key_features: ['', '', '', '', ''],
    listing_url: ''
  });

  useEffect(() => {
    if (propertyData) {
      setFormData({
        price: propertyData.price || '',
        address: propertyData.address || '',
        beds: propertyData.beds || '',
        baths: propertyData.baths || '',
        sqft: propertyData.sqft || '',
        property_summary: propertyData.property_summary || '',
        key_features: propertyData.key_features?.length ? propertyData.key_features : ['', '', '', '', ''],
        listing_url: propertyData.listing_url || ''
      });
    }
  }, [propertyData]);

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSubmit = () => {
    onVerify(formData);
    onClose();
  };

  const needsVerification = (field) => {
    return verificationFields.includes(field) || verificationFields.includes(`${field}_verify`);
  };

  const getConfidenceBadge = (field) => {
    const level = confidence[field];
    if (level === 'high') {
      return <Badge variant="secondary" className="bg-green-100 text-green-800 ml-2">Verified</Badge>;
    } else if (level === 'medium') {
      return <Badge variant="secondary" className="bg-yellow-100 text-yellow-800 ml-2">Check</Badge>;
    } else if (level === 'low' || needsVerification(field)) {
      return <Badge variant="destructive" className="ml-2">Needs Input</Badge>;
    }
    return null;
  };

  const getFieldIcon = (field) => {
    switch (field) {
      case 'price': return <DollarSign className="h-4 w-4" />;
      case 'address': return <MapPin className="h-4 w-4" />;
      case 'beds': return <Bed className="h-4 w-4" />;
      case 'baths': return <Bath className="h-4 w-4" />;
      case 'sqft': return <Ruler className="h-4 w-4" />;
      case 'property_summary': return <FileText className="h-4 w-4" />;
      default: return null;
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[600px]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <AlertTriangle className="h-5 w-5 text-yellow-500" />
            Verify Property Details
          </DialogTitle>
          <DialogDescription>
            Some fields couldn't be extracted accurately. Please verify and complete the highlighted fields.
          </DialogDescription>
        </DialogHeader>

        <div className="grid gap-4 py-4">
          {/* Price */}
          <div className="grid gap-2">
            <div className="flex items-center">
              <Label htmlFor="price" className="flex items-center gap-2">
                {getFieldIcon('price')}
                Listing Price
              </Label>
              {getConfidenceBadge('price')}
            </div>
            <Input
              id="price"
              placeholder="e.g., 599000 or $599,000"
              value={formData.price}
              onChange={(e) => handleChange('price', e.target.value)}
              className={needsVerification('price') ? 'border-yellow-500 bg-yellow-50' : ''}
              data-testid="verify-price-input"
            />
          </div>

          {/* Address */}
          <div className="grid gap-2">
            <div className="flex items-center">
              <Label htmlFor="address" className="flex items-center gap-2">
                {getFieldIcon('address')}
                Property Address
              </Label>
              {getConfidenceBadge('address')}
            </div>
            <Input
              id="address"
              placeholder="e.g., 123 Main St, City, State 12345"
              value={formData.address}
              onChange={(e) => handleChange('address', e.target.value)}
              className={needsVerification('address') ? 'border-yellow-500 bg-yellow-50' : ''}
              data-testid="verify-address-input"
            />
          </div>

          {/* Beds, Baths, Sqft Row */}
          <div className="grid grid-cols-3 gap-4">
            <div className="grid gap-2">
              <div className="flex items-center">
                <Label htmlFor="beds" className="flex items-center gap-2 text-sm">
                  {getFieldIcon('beds')}
                  Beds
                </Label>
                {getConfidenceBadge('beds')}
              </div>
              <Input
                id="beds"
                type="number"
                placeholder="4"
                value={formData.beds}
                onChange={(e) => handleChange('beds', e.target.value)}
                className={needsVerification('beds') ? 'border-yellow-500 bg-yellow-50' : ''}
                data-testid="verify-beds-input"
              />
            </div>

            <div className="grid gap-2">
              <div className="flex items-center">
                <Label htmlFor="baths" className="flex items-center gap-2 text-sm">
                  {getFieldIcon('baths')}
                  Baths
                </Label>
                {getConfidenceBadge('baths')}
              </div>
              <Input
                id="baths"
                type="number"
                step="0.5"
                placeholder="3"
                value={formData.baths}
                onChange={(e) => handleChange('baths', e.target.value)}
                className={needsVerification('baths') ? 'border-yellow-500 bg-yellow-50' : ''}
                data-testid="verify-baths-input"
              />
            </div>

            <div className="grid gap-2">
              <div className="flex items-center">
                <Label htmlFor="sqft" className="flex items-center gap-2 text-sm">
                  {getFieldIcon('sqft')}
                  Sq Ft
                </Label>
                {getConfidenceBadge('sqft')}
              </div>
              <Input
                id="sqft"
                type="number"
                placeholder="2500"
                value={formData.sqft}
                onChange={(e) => handleChange('sqft', e.target.value)}
                className={needsVerification('sqft') ? 'border-yellow-500 bg-yellow-50' : ''}
                data-testid="verify-sqft-input"
              />
            </div>
          </div>

          {/* Property Description */}
          <div className="grid gap-2">
            <div className="flex items-center">
              <Label htmlFor="property_summary" className="flex items-center gap-2">
                <FileText className="h-4 w-4" />
                Property Description
              </Label>
              {getConfidenceBadge('property_summary')}
            </div>
            <Textarea
              id="property_summary"
              placeholder="Enter the property description or listing write-up. This will be used to generate AI-enhanced content and populate templates."
              value={formData.property_summary}
              onChange={(e) => handleChange('property_summary', e.target.value)}
              className={needsVerification('property_summary') ? 'border-yellow-500 bg-yellow-50' : ''}
              rows={4}
              data-testid="verify-description-input"
            />
            <p className="text-xs text-muted-foreground">
              Tip: Paste the property description from the listing to enable AI-generated summaries and key features.
            </p>
          </div>

          {/* Extraction Source Info */}
          {propertyData?.extraction_method && (
            <div className="flex items-center gap-2 text-sm text-muted-foreground bg-muted p-2 rounded">
              <Sparkles className="h-4 w-4" />
              Data extracted via: {propertyData.extraction_method.replace('_', ' ')}
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button onClick={handleSubmit} data-testid="verify-confirm-btn">
            <CheckCircle2 className="h-4 w-4 mr-2" />
            Confirm & Continue
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default VerifyPropertyModal;
